/* eslint-disable @typescript-eslint/no-explicit-any */
import { BeneficiaryType } from "@/types/beneficiaries";
import { isBoardingProcessingStatue } from "@/types/employeeState";
import {
  BeneficiaryInvestmentProfile,
  DropdownInvestmentOption,
  InvestmentOptionAPIType,
} from "@/types/investmentProfile";

/**
 * Calculates investment option recommendations for beneficiaries based on their age
 * Note: Only applies to Texas investment plans.
 */
export const calculateInvestmentOptionRecommendations = (
  beneficiaries: BeneficiaryType[],
  options: InvestmentOptionAPIType[],
): InvestmentOptionAPIType[] => {
  // const hasMichiganOptions = options.some(
  //   (option) => option.state529 === "UTAH"
  // );

  // For Michigan, don't provide age-based recommendations
  // Return the first available option as a default for each beneficiary
  // Review later since we have now added age based recommendations for Michigan as well with Deepesh
  return beneficiaries.map(
    () =>
      options[0] || {
        portfolioId: 0,
        portfiloShortName: "Unknown",
        portfolioName: "Unknown",
        portfolioPooledAcctId: "",
        portfolioCashAcctId: "",
        unrealizedGainAccountId: "",
        unrealizedLossAccountId: "",
        realizedGainAccountId: "",
        realizedLossAccountId: "",
        model: "",
        productCode: "",
        description: "",
        totalUnitsHeld: 0,
        totalHoldingBalance: 0,
        benefitType: "",
        additionalDetails: "",
        investmentUpto: "0",
        state529: "Unknown",
        currentNavValue: 0,
        navTrend: "",
        latestNavDate: "",
        percentage: 0,
        totalInvestment: 0,
        no_of_Subscribers: 0,
      },
  );
};

/**
 * Finds the most appropriate investment option based on beneficiary's age
 * Note: This function is only meant to work with Texas investment plans
 */
export const findTargetInvestmentOption = (
  age: number,
  options: InvestmentOptionAPIType[],
): InvestmentOptionAPIType => {
  const formattedOptions = options.filter((item) => item.portfolioId < 32);

  const exactMatch = formattedOptions.find((option) => {
    if (option.portfolioName.includes("and Over")) {
      const minAge = parseInt(option.portfolioName.split(" ")[0]);
      return age >= minAge;
    }
    const rangeParts = option.portfolioName.split(" ")[0].split("-");
    if (rangeParts.length === 1) {
      if (option.portfolioName.includes("0-3")) {
        return age >= 0 && age <= 3;
      }
      const singleAge = parseInt(rangeParts[0]);
      return singleAge === age;
    } else if (rangeParts.length === 2) {
      const lowerAge = parseInt(rangeParts[0]);
      const upperAge = parseInt(rangeParts[1]);
      return age >= lowerAge && age <= upperAge;
    }
    return false;
  });

  if (exactMatch) {
    return exactMatch;
  }

  // If no exact match, find the closest appropriate option
  return formattedOptions.reduce(
    (
      closestOption: InvestmentOptionAPIType,
      currentOption: InvestmentOptionAPIType,
    ): InvestmentOptionAPIType => {
      if (
        Math.abs(parseInt(currentOption.investmentUpto) - age) <
        Math.abs(parseInt(closestOption.investmentUpto) - age)
      ) {
        return currentOption;
      }
      return closestOption;
    },
    formattedOptions[0],
  );
};

export const filterTexasInvestmentOptions = (
  investmentOptions: InvestmentOptionAPIType[],
): InvestmentOptionAPIType[] => {
  return investmentOptions.filter(
    (investmentOption: InvestmentOptionAPIType) =>
      investmentOption.state529 === "TEXAS",
  );
};

export const filterMichiganInvestmentOptions = (
  investmentOptions: InvestmentOptionAPIType[],
): InvestmentOptionAPIType[] => {
  return investmentOptions;
  //  .filter(
  //   (investmentOption: InvestmentOptionAPIType) =>
  //     investmentOption.state529 === "UTAH"
  // );
};

export const formatInvestmentOptionsForDropdown = (
  investmentOptions: InvestmentOptionAPIType[],
): DropdownInvestmentOption[] => {
  return investmentOptions.map((option: InvestmentOptionAPIType) => ({
    label: option.portfolioName,
    value: option.portfiloShortName,
  }));
};

export const getInvestmentOptionDetails = (
  investmentOption: string,
  investmentOptionsFromAPI: InvestmentOptionAPIType[],
): InvestmentOptionAPIType | null => {
  const option = investmentOptionsFromAPI?.find(
    (option: any) => option.portfiloShortName === investmentOption,
  );
  return option ? option : null;
};

export const formatBeneficiariesForInvestmentProfile = (
  beneficiaries: BeneficiaryType[],
  filteredInvestmentOptions: InvestmentOptionAPIType[],
  recommendations: InvestmentOptionAPIType[],
): BeneficiaryInvestmentProfile[] => {
  const hasMichiganOptions = filteredInvestmentOptions;
  // .some(
  //   (option) => option.state529 === "UTAH"
  // );

  return beneficiaries
    .filter(
      (item: any) => !isBoardingProcessingStatue(item.minorEnrolledStatus),
    )
    .map((beneficiary: BeneficiaryType, index: number) => {
      let selectedOption = null;
      if (beneficiary.investmentOption) {
        selectedOption = filteredInvestmentOptions.find(
          (option: InvestmentOptionAPIType) =>
            option.portfolioName === beneficiary.investmentOption,
        );
      }

      const recommendedOption = recommendations[index];

      const fallbackOption = filteredInvestmentOptions[0] || {
        portfiloShortName: "Unknown",
        portfolioName: "Unknown",
      };

      const safeRecommendedOption =
        recommendedOption && recommendedOption.portfiloShortName
          ? recommendedOption
          : fallbackOption;

      if (hasMichiganOptions && !selectedOption) {
        const recommendedYear = calculateRecommendedAcademicYear(
          beneficiary.dateOfBirth,
        );
        const recommendedOption = getRecommendedOptionForYear(
          filteredInvestmentOptions,
          recommendedYear,
        );

        return {
          id: beneficiary.minorAccountID,
          name: beneficiary.firstName + " " + beneficiary.lastName,
          selectedInvestmentOptionID:
            recommendedOption?.portfiloShortName || "",
          selectedInvestmentOptionName:
            recommendedOption?.portfolioName || "Please select an option",
          selectedAcademicTargetYear: recommendedYear,
          expanded: false,
          investmentPlanType: recommendedOption ? "recommendation" : "custom",
          employeeMinorContributionInDollar:
            beneficiary.employeeMinorContributionInDollar,
          employerMinorContributionInDollar:
            beneficiary.employerMinorContributionInDollar,
        };
      }

      const recommendedYear = hasMichiganOptions
        ? calculateRecommendedAcademicYear(beneficiary.dateOfBirth)
        : safeRecommendedOption.investmentUpto;

      return {
        id: beneficiary.minorAccountID,
        name: beneficiary.firstName + " " + beneficiary.lastName,
        selectedInvestmentOptionID: selectedOption
          ? selectedOption.portfiloShortName
          : safeRecommendedOption.portfiloShortName,
        selectedInvestmentOptionName: selectedOption
          ? selectedOption.portfolioName
          : safeRecommendedOption.portfolioName,
        selectedAcademicTargetYear: selectedOption
          ? selectedOption.investmentUpto
          : recommendedYear,
        expanded: false,
        investmentPlanType: selectedOption
          ? "custom"
          : hasMichiganOptions
            ? "custom"
            : "recommendation",
        employeeMinorContributionInDollar:
          beneficiary.employeeMinorContributionInDollar,
        employerMinorContributionInDollar:
          beneficiary.employerMinorContributionInDollar,
      };
    });
};

/**
 * Creates recommended options mapping for beneficiaries
 * Note: Only creates recommendations for Texas plans. For Michigan, returns empty recommendations.
 * Review later if we are still using this func with Deepesh
 */
export const createRecommendedOptionsMap = (
  beneficiaries: BeneficiaryType[],
  recommendations: InvestmentOptionAPIType[],
): { [beneficiaryId: string]: string } => {
  const recommendedOptionsMap: { [beneficiaryId: string]: string } = {};

  const hasRecommendations = recommendations && recommendations.length > 0;

  if (!hasRecommendations) {
    return recommendedOptionsMap;
  }

  beneficiaries
    .filter(
      (item: any) => !isBoardingProcessingStatue(item.minorEnrolledStatus),
    )
    .forEach((beneficiary: BeneficiaryType, index: number) => {
      const recommendedOption = recommendations[index];

      if (recommendedOption && recommendedOption.portfiloShortName) {
        recommendedOptionsMap[beneficiary.minorAccountID] =
          recommendedOption.portfiloShortName;
      } else {
        recommendedOptionsMap[beneficiary.minorAccountID] = "Unknown";
      }
    });

  return recommendedOptionsMap;
};

export const formatBeneficiariesForAPI = (
  beneficiaries: BeneficiaryInvestmentProfile[],
  userAccountID: string,
  employeeID: string,
  sootchyEmployerID: string,
) => {
  return beneficiaries.map((beneficiary: BeneficiaryInvestmentProfile) => ({
    userAccountID: userAccountID,
    minorAccountID: beneficiary.id,
    employeeID: employeeID,
    sootchyEmployerID: parseInt(sootchyEmployerID),
    investmentOption: beneficiary.selectedInvestmentOptionID,
    createdUserAccountID: userAccountID,
    sourceProcess: "WebApplication",
    changeReason: "Save Investment Option",
  }));
};

/**
 * Determines investment plan type based on recommendation
 */
export const determineInvestmentPlanType = (
  selectedOption: string,
  recommendedOption: string,
): "recommendation" | "custom" => {
  return selectedOption === recommendedOption ? "recommendation" : "custom";
};

export const getUniqueAcademicYears = (
  investmentOptions: InvestmentOptionAPIType[],
  recommendedYear?: string,
): DropdownInvestmentOption[] => {
  const uniqueYears = [
    ...new Set(investmentOptions.map((option) => option.investmentUpto)),
  ]
    .filter((year) => year && year !== "0")
    .sort();

  const currentYear = new Date().getFullYear();

  if (!recommendedYear) {
    return uniqueYears.map((year) => ({
      label: year,
      value: year,
    }));
  }

  const targetYearNum = parseInt(recommendedYear);
  const validYears: number[] = [];
  const startYear = Math.max(targetYearNum, currentYear);

  for (let i = 0; i < 3; i++) {
    validYears.push(startYear + i);
  }

  return validYears.map((year) => ({
    label: year.toString(),
    value: year.toString(),
  }));
};

export const calculateRecommendedAcademicYear = (
  dateOfBirth: string,
): string => {
  const birthDate = new Date(dateOfBirth);
  const birthYear = birthDate.getFullYear();
  const currentYear = new Date().getFullYear();
  const expectedStartYear = birthYear + 18;
  const targetYear = Math.max(expectedStartYear, currentYear);
  return targetYear.toString();
};

/**
 * Filters investment options by academic target year
 */
export const filterOptionsByAcademicYear = (
  investmentOptions: InvestmentOptionAPIType[],
  targetYear: string,
): InvestmentOptionAPIType[] => {
  return investmentOptions.filter(
    (option) => option.investmentUpto === targetYear,
  );
};

/**
 * Get the recommended investment option (Full Benefits) for a given year
 */
export const getRecommendedOptionForYear = (
  investmentOptions: InvestmentOptionAPIType[],
  targetYear: string,
): InvestmentOptionAPIType | null => {
  const optionsForYear = filterOptionsByAcademicYear(
    investmentOptions,
    targetYear,
  );
  // Show Full benefits as the recommended option
  const fullBenefitsOption = optionsForYear.find(
    (option) => option.benefitType === "Full",
  );
  return fullBenefitsOption || optionsForYear[0] || null;
};
